import type { ReactNode } from 'react';
import { memo, useMemo } from 'react';
import noop from 'lodash/noop';
import { createContext, useContextSelector } from 'use-context-selector';
import type { HydrateProjectsQuery } from '@frameio/workspaces/projects/gql/generated/HydrateProjects';
import { useRecentProjects } from '@frameio/workspaces/shared';
import { useRecentSearches } from '../hooks/useRecentSearches';

const MAX_RECENT_PROJECTS = 4;

type QuickSearchDataState = {
  addRecentSearch: (query: string) => void;
  hasRecentProjects: boolean;
  hasRecentSearches: boolean;
  recentProjects: NonNullable<
    NonNullable<HydrateProjectsQuery['account']>['projects']
  >['nodes'];
  recentSearches: string[];
  removeAllRecentSearches: () => void;
  removeRecentSearch: (query: string) => void;
};

const defaultDataState: QuickSearchDataState = {
  addRecentSearch: noop,
  hasRecentProjects: false,
  hasRecentSearches: false,
  recentProjects: [],
  recentSearches: [],
  removeAllRecentSearches: noop,
  removeRecentSearch: noop,
};

export const QuickSearchDataContext = createContext(defaultDataState);

/**
 * Loads data and exposes handlers necessary for the inital state of QuickSearch
 * including recent searches and recent projects
 */
export const QuickSearchDataProvider = memo(
  ({ children }: { children: ReactNode }) => {
    const {
      addRecentSearch,
      recentSearches,
      removeAllRecentSearches,
      removeRecentSearch,
    } = useRecentSearches();
    const allRecentProjects = useRecentProjects();

    const recentProjects = useMemo(
      () => allRecentProjects?.slice(0, MAX_RECENT_PROJECTS) ?? [],
      [allRecentProjects]
    );

    const hasRecentSearches = recentSearches.length > 0;
    const hasRecentProjects = recentProjects.length > 0;

    const value = useMemo(
      () => ({
        addRecentSearch,
        hasRecentProjects,
        hasRecentSearches,
        recentProjects,
        recentSearches,
        removeAllRecentSearches,
        removeRecentSearch,
      }),
      [
        addRecentSearch,
        hasRecentProjects,
        hasRecentSearches,
        recentProjects,
        recentSearches,
        removeAllRecentSearches,
        removeRecentSearch,
      ]
    );

    return (
      <QuickSearchDataContext.Provider value={value}>
        {children}
      </QuickSearchDataContext.Provider>
    );
  }
);

export const useQuickSearchModalData = () => {
  const recentSearches = useContextSelector(
    QuickSearchDataContext,
    (value) => value?.recentSearches
  );

  const addRecentSearch = useContextSelector(
    QuickSearchDataContext,
    (value) => value?.addRecentSearch
  );

  const removeAllRecentSearches = useContextSelector(
    QuickSearchDataContext,
    (value) => value?.removeAllRecentSearches
  );

  const removeRecentSearch = useContextSelector(
    QuickSearchDataContext,
    (value) => value?.removeRecentSearch
  );

  const recentProjects = useContextSelector(
    QuickSearchDataContext,
    (value) => value?.recentProjects
  );

  const hasRecentSearches = useContextSelector(
    QuickSearchDataContext,
    (value) => value?.hasRecentSearches
  );

  const hasRecentProjects = useContextSelector(
    QuickSearchDataContext,
    (value) => value?.hasRecentProjects
  );

  return {
    addRecentSearch,
    hasRecentProjects,
    hasRecentSearches,
    recentProjects,
    recentSearches,
    removeAllRecentSearches,
    removeRecentSearch,
  };
};
